// Cover system (GDD §4.4): directional cover (walls/sandbags/craters — reduction only
// against fire crossing the cover line), area cover (ruins/garrison shells —
// omnidirectional lighter reduction + sight penalty), and negative cover (roads —
// extra damage). Models auto-snap to cover slots near their ordered position, and the
// UI previews cover quality before you commit an order.

import { angleDelta, angleOf, dist, type Vec2 } from "../engine/vec";
import type { CoverDef, Faction, Model, Squad } from "./types";
import type { World } from "./world";

export interface CoverSlot {
  pos: Vec2;
  dir: number; // protecting direction: angle toward the threats this slot blocks
  value: number; // incoming-damage multiplier (0.5 = -50%)
  pieceIndex: number;
  taken: number; // model id, -1 free
}

const SLOT_STEP = 26;
const SLOT_OFFSET = 16;

/** Sample directional cover into a flat list of occupiable slots (both sides). */
export function buildCoverSlots(cover: CoverDef[]): CoverSlot[] {
  const slots: CoverSlot[] = [];
  cover.forEach((c, idx) => {
    if (c.kind !== "wall" && c.kind !== "sandbag" && c.kind !== "crater") return;
    const a = c.a!;
    const b = c.b!;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    const nx = -dy / len; // segment normal
    const ny = dx / len;
    const steps = Math.max(1, Math.round(len / SLOT_STEP));
    for (let i = 0; i <= steps; i++) {
      const t = steps === 0 ? 0.5 : i / steps;
      const px = a.x + dx * t;
      const py = a.y + dy * t;
      // slot on +normal side is protected from threats on the -normal side
      slots.push({
        pos: { x: px + nx * SLOT_OFFSET, y: py + ny * SLOT_OFFSET },
        dir: angleOf({ x: -nx, y: -ny }),
        value: c.value,
        pieceIndex: idx,
        taken: -1,
      });
      slots.push({
        pos: { x: px - nx * SLOT_OFFSET, y: py - ny * SLOT_OFFSET },
        dir: angleOf({ x: nx, y: ny }),
        value: c.value,
        pieceIndex: idx,
        taken: -1,
      });
    }
  });
  return slots;
}

// ---- geometry helpers ------------------------------------------------------

function pointInRect(p: Vec2, x: number, y: number, w: number, h: number): boolean {
  return p.x >= x && p.x <= x + w && p.y >= y && p.y <= y + h;
}

function pointInCircle(p: Vec2, cx: number, cy: number, r: number): boolean {
  return (p.x - cx) ** 2 + (p.y - cy) ** 2 <= r * r;
}

function segIntersectsRect(a: Vec2, b: Vec2, x: number, y: number, w: number, h: number): boolean {
  if (pointInRect(a, x, y, w, h) || pointInRect(b, x, y, w, h)) return true;
  const edges: [Vec2, Vec2][] = [
    [{ x, y }, { x: x + w, y }],
    [{ x: x + w, y }, { x: x + w, y: y + h }],
    [{ x: x + w, y: y + h }, { x, y: y + h }],
    [{ x, y: y + h }, { x, y }],
  ];
  for (const [p, q] of edges) if (segSeg(a, b, p, q)) return true;
  return false;
}

function segIntersectsCircle(a: Vec2, b: Vec2, cx: number, cy: number, r: number): boolean {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const fx = a.x - cx;
  const fy = a.y - cy;
  const A = dx * dx + dy * dy;
  const B = 2 * (fx * dx + fy * dy);
  const C = fx * fx + fy * fy - r * r;
  let disc = B * B - 4 * A * C;
  if (disc < 0) return false;
  disc = Math.sqrt(disc);
  const t1 = (-B - disc) / (2 * A);
  const t2 = (-B + disc) / (2 * A);
  return (t1 >= 0 && t1 <= 1) || (t2 >= 0 && t2 <= 1);
}

function segSeg(a: Vec2, b: Vec2, c: Vec2, d: Vec2): boolean {
  const d1 = cross(c, d, a);
  const d2 = cross(c, d, b);
  const d3 = cross(a, b, c);
  const d4 = cross(a, b, d);
  return ((d1 > 0) !== (d2 > 0)) && ((d3 > 0) !== (d4 > 0));
}

function cross(o: Vec2, a: Vec2, b: Vec2): number {
  return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
}

function distToSeg(p: Vec2, a: Vec2, b: Vec2): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const l2 = dx * dx + dy * dy;
  if (l2 === 0) return dist(p, a);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

// ---- runtime queries -------------------------------------------------------

/** Damage multiplier applied to a shot hitting `target` fired from `attackerPos`. */
export function coverMultiplierAt(world: World, target: Model, attackerPos: Vec2): number {
  let reduction = 1; // best (lowest) reduction wins among area+directional
  let roadPenalty = 1;
  const p = target.pos;

  for (const c of world.cover) {
    if (c.kind === "ruin") {
      if (pointInCircle(p, c.x!, c.y!, c.radius!)) reduction = Math.min(reduction, c.value);
    } else if (c.kind === "building" && c.garrison) {
      if (pointInRect(p, c.x!, c.y!, c.w!, c.h!)) reduction = Math.min(reduction, c.value);
    } else if (c.kind === "road") {
      if (pointInRect(p, c.x!, c.y!, c.w!, c.h!)) roadPenalty = Math.max(roadPenalty, c.value);
    }
  }

  // Directional cover: the segment must lie between attacker and target and the
  // target must be hunkered close to it.
  for (const c of world.cover) {
    if (c.kind !== "wall" && c.kind !== "sandbag" && c.kind !== "crater") continue;
    if (distToSeg(p, c.a!, c.b!) > 34) continue;
    if (segSeg(attackerPos, p, c.a!, c.b!)) {
      reduction = Math.min(reduction, c.value);
    }
  }

  // Also honor an explicitly assigned slot's facing (handles the "hugging the wall
  // from the correct side" case even when the segment test is marginal).
  if (target.coverDir !== null && target.coverValue < 1) {
    const toAttacker = angleOf({ x: attackerPos.x - p.x, y: attackerPos.y - p.y });
    if (Math.abs(angleDelta(toAttacker, target.coverDir)) < 1.4) {
      reduction = Math.min(reduction, target.coverValue);
    }
  }

  return reduction * roadPenalty;
}

/** LOS between two world points: full block by solid buildings; partial by ruins. */
export function losInfo(world: World, from: Vec2, to: Vec2): { blocked: boolean; sightPenalty: number } {
  let sightPenalty = 0;
  for (const c of world.cover) {
    if (c.kind === "building" && !c.garrison) {
      if (segIntersectsRect(from, to, c.x!, c.y!, c.w!, c.h!)) return { blocked: true, sightPenalty: 1 };
    } else if (c.kind === "ruin" && c.sightBlock) {
      if (segIntersectsCircle(from, to, c.x!, c.y!, c.radius!)) sightPenalty += c.sightBlock * 0.4;
    } else if (c.kind === "building" && c.garrison && c.sightBlock) {
      if (segIntersectsRect(from, to, c.x!, c.y!, c.w!, c.h!)) sightPenalty += 0.25;
    }
  }
  return { blocked: false, sightPenalty: Math.min(0.6, sightPenalty) };
}

export function isOnRoad(world: World, p: Vec2): boolean {
  for (const c of world.cover)
    if (c.kind === "road" && pointInRect(p, c.x!, c.y!, c.w!, c.h!)) return true;
  return false;
}

/** Threat direction for a squad: toward nearest enemy, else toward enemy base. */
export function threatDir(world: World, sq: Squad): number {
  const enemy = world.nearestEnemySquad(sq.faction, sq.center, 900);
  if (enemy) return angleOf({ x: enemy.center.x - sq.center.x, y: enemy.center.y - sq.center.y });
  const hq = world.hqOf(world.enemyOf(sq.faction));
  if (hq) return angleOf({ x: hq.pos.x - sq.center.x, y: hq.pos.y - sq.center.y });
  return sq.faction === "legion" ? 0 : Math.PI;
}

/**
 * Global cover assignment pass for squads that are settled (holding / arrived) and
 * not broken. Greedily assigns each model to the nearest free slot that faces the
 * threat, within snap radius. Vehicles/heroes don't take infantry cover slots.
 */
export function assignCoverForSettling(world: World, snapRadius: number): void {
  for (const s of world.coverSlots) s.taken = -1;
  for (const sq of world.squads) {
    if (sq.dead) continue;
    const settled = isSettled(sq);
    if (!settled || sq.broken || sq.frenzy || sq.def.role === "vehicle") {
      for (const m of sq.models) m.coverTarget = null;
      continue;
    }
    const tdir = threatDir(world, sq);
    for (const m of sq.models) {
      if (!m.alive) continue;
      let best: CoverSlot | null = null;
      let bestScore = Infinity;
      for (const slot of world.coverSlots) {
        if (slot.taken !== -1) continue;
        if (Math.abs(angleDelta(slot.dir, tdir)) > 1.3) continue; // must face the threat
        const d = dist(slot.pos, m.pos);
        if (d > snapRadius) continue;
        const score = d * slot.value; // prefer close + strong (low value = strong)
        if (score < bestScore) {
          bestScore = score;
          best = slot;
        }
      }
      if (best) {
        best.taken = m.id;
        m.coverTarget = { ...best.pos };
        m.coverDir = best.dir;
      } else {
        m.coverTarget = null;
        m.coverDir = null;
      }
    }
  }
}

function isSettled(sq: Squad): boolean {
  if (sq.order.type === "move" || sq.order.type === "attackmove") {
    return sq.path.length === 0 && sq.pathTarget === null;
  }
  return true; // stop / hold / attack / capture → settle in place
}

/** UI cover-quality preview at a hovered destination → 0..3 shield pips. */
export function coverPreviewAt(world: World, pos: Vec2, _faction: Faction): number {
  let best = 1;
  for (const c of world.cover) {
    if (c.kind === "ruin" && pointInCircle(pos, c.x!, c.y!, c.radius! + 20)) best = Math.min(best, c.value);
    else if (c.kind === "building" && c.garrison && pointInRect(pos, c.x! - 20, c.y! - 20, c.w! + 40, c.h! + 40)) best = Math.min(best, c.value);
    else if ((c.kind === "wall" || c.kind === "sandbag" || c.kind === "crater") && distToSeg(pos, c.a!, c.b!) < 60) best = Math.min(best, c.value);
  }
  if (best >= 0.95) return 0;
  if (best >= 0.65) return 1;
  if (best >= 0.5) return 2;
  return 3;
}
